import breakpoints from '@configs/breakpoints';
import FilterSectionAttributeChoice from '@features/catalog/filter/FilterSectionAttributeChoice';
import { clearUrlFromFilterParams } from '@features/catalog/filter/services';
import AnimateHeight from '@store/components/AnimateHeight';
import useComponentVisible from '@store/hooks/useComponentVisible';
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import FilterSectionAttributeRange from './FilterSectionAttributeRange';
import { IFilterAttributeChoice, IFilterAttributeRange, ROUTE_KEY_PAGE } from './types';

interface IProps {
  attributes: (IFilterAttributeChoice | IFilterAttributeRange)[];
}

export default function Filter(props: IProps) {
  const { t } = useTranslation();

  const { isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

  const onReset = () => {
    const url = new URL(location.href, location.origin);
    clearUrlFromFilterParams(url);

    location.href = url.toString();
  };

  const onChangeChoice = (urlKey: string, id: string, checked: boolean) => {
    const url = new URL(location.href, location.origin);

    // Ak obsahuje URL parameter s danym klucom pre filter, tak ho musime spracovat
    if (url.searchParams.has(urlKey)) {
      const parts = []; // Pole vyslednych casti pre dany parameter
      let hasBeenFound = false;

      for (const value of url.searchParams.getAll(urlKey)) {
        // Ak je len z jednej hodnoty ( neobsahuje , )
        if (value.indexOf(',') === -1) {
          // Vzdy pridame hodnotu do vysledkov
          parts.push(value);
          if (value !== id) continue;
          hasBeenFound = true;
          // Ak bol atribut odciarknuty, tak odstranime poslednu pridanu hodnotu ( jeho hodnota )
          if (!checked) parts.pop();
          continue;
        }

        for (const part of value.split(',')) {
          parts.push(part);
          if (part !== id) continue;
          hasBeenFound = true;
          if (!checked) parts.pop();
        }
      }

      // Ak sa nenachadzal ani v jednej casti parametrov a je oznaceny na pridanie, tak ho pridame
      if (!hasBeenFound && checked) parts.push(id);

      // Odstranime cely segment parametrov pre dany kluc
      url.searchParams.delete(urlKey);
      // Ak su nejake parametre na pridanie, tak ich pridame do URL
      if (parts.length > 0) url.searchParams.set(decodeURIComponent(urlKey), parts.join(','));
    } else {
      url.searchParams.append(urlKey, id);
    }

    url.searchParams.delete(ROUTE_KEY_PAGE);
    location.href = url.toString();
  };

  const onChangeRange = (urlKey: string, min?: number, max?: number) => {
    const url = new URL(location.href, location.origin);

    if (url.searchParams.has(urlKey)) {
      url.searchParams.delete(urlKey);
    }

    if (min !== undefined || max !== undefined) {
      url.searchParams.append(urlKey, `${min ?? ''},${max ?? ''}`);
    }

    url.searchParams.delete(ROUTE_KEY_PAGE);
    location.href = decodeURI(url.toString());
  };

  useEffect(() => {
    handler();
    window.addEventListener('resize', handler, false);
    return () => window.removeEventListener('resize', handler);

    function handler() {
      const mediaQueryList = window.matchMedia(`(min-width: ${breakpoints.laptop}px)`);
      if (mediaQueryList.matches) {
        setIsComponentVisible(true);
      }
    }
  }, [setIsComponentVisible]);

  useEffect(() => {
    if (props.attributes.length === 0) return;

    const catalogContainer = document.querySelector<HTMLElement>('.catalog-container');
    catalogContainer?.classList?.remove('without-filter');
  }, [props.attributes.length]);

  if (props.attributes.length === 0) return <></>;

  return (
    <div>
      <button
        className="btn btn-primary w-full laptop:hidden text-lg"
        type="button"
        onClick={() => setIsComponentVisible(val => !val)}>
        <span className="icon mr-2">
          <i className="fa-solid fa-filter fa-sm" />
        </span>
        {t('Filter')}
      </button>
      <AnimateHeight isOpen={isComponentVisible}>
        <div className="flex flex-col gap-y-2 laptop:gap-y-2 my-4 laptop:mt-0 p-2 laptop:py-0 border-b-2 laptop:border-b-0">
          <div className="flex flex-col gap-y-2">
            {props.attributes.map(attr => {
              if ('choices' in attr && attr.choices.length > 0)
                return (
                  <FilterSectionAttributeChoice
                    key={attr.queryKey}
                    {...attr}
                    onChange={onChangeChoice}
                  />
                );

              if ('rangeSteps' in attr && attr.rangeSteps.length > 0)
                return (
                  <FilterSectionAttributeRange
                    key={attr.queryKey}
                    {...attr}
                    onChange={onChangeRange}
                  />
                );

              return <Fragment key={attr.queryKey}></Fragment>;
            })}
          </div>
          <div className="flex items-center justify-center">
            <button className="btn btn-simple w-1/2" type="reset" onClick={onReset}>
              {t('ToCancel')}
            </button>
          </div>
        </div>
      </AnimateHeight>
    </div>
  );
}
